import React from 'react';

import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import InfoIcon from '@material-ui/icons/Info';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';

import { ReactComponent as ShareIcon } from '../../assets/icons/Share.svg';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import AddToHomeScreenIcon from '@material-ui/icons/AddToHomeScreen';

const useStyles = makeStyles((theme) => ({
  dialogTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  icon: {
    marginLeft: theme.spacing(2),
    fontSize: 32,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  device: string;
};

export default function AddToHomeScreenDialog({
  open,
  onClose,
  device,
}: Props) {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <InfoIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Add to Homescreen
      </DialogTitle>
      {device === 'iOS Device' && (
        <DialogContent>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">
              1. Tap the More button to access options {'(...)'}
            </Typography>
          </div>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">2. Tap "Share"</Typography>
            <SvgIcon component={ShareIcon} className={classes.icon} />
          </div>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">3. Tap "Add to Home Screen"</Typography>
            <AddBoxOutlinedIcon className={classes.icon} />
          </div>
        </DialogContent>
      )}
      {device === 'Android Device' && (
        <DialogContent>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">
              1. Tap the More button to access options
            </Typography>
            <MoreVertIcon className={classes.icon} />
          </div>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">2. Tap "Add to Home Screen"</Typography>
            <AddToHomeScreenIcon className={classes.icon} />
          </div>
          <div className={classes.dialogTextContainer}>
            <Typography variant="body2">3. Tap "Install"</Typography>
          </div>
        </DialogContent>
      )}
      {device === 'Unknown Device' && (
        <>
          <DialogContent>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>For Windows (Chrome, Edge, Firefox)</strong>
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Drag-and-Drop:</strong> Open the website, resize your
                browser window to see the desktop, then click and drag the
                padlock icon (or similar) from the address bar onto the desktop.
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Google Chrome "Create Shortcut": </strong>
                {`With the site open, click the three-dot menu (top right) -> More tools -> Create shortcut. You can also select "Open as window".`}
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Microsoft Edge "Install as App": </strong>
                {`Go to the website, click the three-dot menu -> Apps -> Install this site as an app. This adds it to the Start Menu and Desktop, opening in its own window.`}
              </Typography>
            </div>
          </DialogContent>
          <DialogContent>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>For MacOS (Safari, Chrome)</strong>
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Drag-and-Drop to Desktop:</strong> Navigate to the site,
                then click and drag the padlock icon or the URL text from the
                address bar onto the desktop. This creates a .webloc file.
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Safari "Add to Dock" (Sonoma+): </strong>
                {`Open the site in Safari, click the Share icon (top right) -> Add to Dock. Click Add to create a web app that opens in a dedicated window.`}
              </Typography>
            </div>
            <div className={classes.dialogTextContainer}>
              <Typography variant="body2">
                <strong>Chrome "Create Shortcut": </strong>
                {`Follow the same steps as for Windows (three-dot menu > More tools > Create shortcut). The shortcut is placed in your ~/Applications/Chrome Apps folder and can be dragged to the Dock. `}
              </Typography>
            </div>
          </DialogContent>
        </>
      )}
      <DialogActions>
        <Button
          onClick={(event) => {
            if (onClose) {
              onClose(event, 'backdropClick');
            }
          }}
          color="primary"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
